import { personneParId } from './modele.js';
import type { DonneesArbre, Personne } from './types.js';

/**
 * Phrase des actes de notoriété à obtenir (D04, équipe). Une succession dont la notoriété n'est ni faite ni close reste
 * à obtenir. Aucune phrase sans succession à traiter : rien n'est inventé. Jamais montrée hors équipe : le mandant n'est
 * pas nommé aux autres héritiers.
 */
const COMMENCE_PAR_VOYELLE = /^[aeiouyàâäéèêëîïôöùûüÿœæh]/i;

const majuscule = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** « d’Émile C. », « de Marie B. » : l'article s'élide devant une voyelle ou un h. Sans prénom connu : le lien de parenté, entre guillemets. */
function designation(p: Personne | undefined): string {
  const prenom = p?.firstNames.trim().split(/\s+/)[0] ?? '';
  if (!p || prenom === '') {
    const lien = p?.relationLabel?.trim();
    return `de « ${lien && lien !== '' ? lien : 'personne à identifier'} »`;
  }
  const initiale = p.lastName.trim().charAt(0);
  const nom = initiale === '' ? majuscule(prenom) : `${majuscule(prenom)} ${initiale.toUpperCase()}.`;
  return `${COMMENCE_PAR_VOYELLE.test(prenom) ? 'd’' : 'de '}${nom}`;
}

const liste = (elements: readonly string[]): string =>
  elements.length <= 1 ? (elements[0] ?? '') : `${elements.slice(0, -1).join(', ')} et ${elements[elements.length - 1]}`;

export function phraseNotoriete(donnees: DonneesArbre, pour: 'equipe' | 'famille' = 'equipe'): string | null {
  if (pour !== 'equipe') return null;
  const aObtenir = donnees.successions
    .filter((s) => s.status === 'ouverte')
    .sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  if (aObtenir.length === 0) return null;
  const noms = aObtenir.map((s) => designation(personneParId(donnees, s.deceasedPersonId)));
  const phrase =
    aObtenir.length === 1
      ? `Acte de notoriété à obtenir pour la succession ${noms[0]}`
      : `${aObtenir.length} actes de notoriété à obtenir : successions ${liste(noms)}`;
  // L'initiale du nom porte déjà son point : il fait office de point final.
  return phrase.endsWith('.') ? phrase : `${phrase}.`;
}
